import { useMemo } from "react";
import {
  IconLock,
  IconLockOpen,
  IconDownload,
  IconCamera,
  IconCameraRotate,
  IconTrash,
  IconPlus,
  IconStarFilled,
  IconEye,
  IconEyeOff,
  IconChecklist,
} from "@tabler/icons-react";
import { format, isToday, isYesterday } from "date-fns";
import type { components } from "@/api/generated/schema";
import { utc } from "@/lib/time";

type AuditLogItem = components["schemas"]["AuditLogResponse"];

const ACTION_CONFIG: Record<
  string,
  { label: string; icon: typeof IconLock; bgColor: string; iconColor: string }
> = {
  build_created: {
    label: "Build created",
    icon: IconPlus,
    bgColor: "bg-emerald-100 dark:bg-emerald-950/50",
    iconColor: "text-emerald-600 dark:text-emerald-400",
  },
  build_signed_off: {
    label: "Build signed off",
    icon: IconLock,
    bgColor: "bg-green-100 dark:bg-green-950/50",
    iconColor: "text-green-600 dark:text-green-400",
  },
  build_unlocked: {
    label: "Build unlocked",
    icon: IconLockOpen,
    bgColor: "bg-slate-100",
    iconColor: "text-slate-600",
  },
  calibration_fetched: {
    label: "Calibration fetched",
    icon: IconDownload,
    bgColor: "bg-slate-100",
    iconColor: "text-slate-600",
  },
  calibration_manual_created: {
    label: "Manual calibration created",
    icon: IconPlus,
    bgColor: "bg-slate-100",
    iconColor: "text-slate-600",
  },
  calibration_state_changed: {
    label: "Set as golden",
    icon: IconStarFilled,
    bgColor: "bg-amber-100 dark:bg-amber-950/50",
    iconColor: "text-amber-500",
  },
  calibration_signed_off: {
    label: "Calibration signed off",
    icon: IconLock,
    bgColor: "bg-green-100 dark:bg-green-950/50",
    iconColor: "text-green-600 dark:text-green-400",
  },
  calibration_unlocked: {
    label: "Calibration unlocked",
    icon: IconLockOpen,
    bgColor: "bg-slate-100",
    iconColor: "text-slate-600",
  },
  calibration_hidden: {
    label: "Calibration hidden",
    icon: IconEyeOff,
    bgColor: "bg-slate-100",
    iconColor: "text-slate-600",
  },
  calibration_unhidden: {
    label: "Calibration unhidden",
    icon: IconEye,
    bgColor: "bg-slate-100",
    iconColor: "text-slate-600",
  },
  photo_uploaded: {
    label: "Photo uploaded",
    icon: IconCamera,
    bgColor: "bg-blue-100 dark:bg-blue-950/50",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
  photo_retaken: {
    label: "Photo retaken",
    icon: IconCameraRotate,
    bgColor: "bg-blue-100 dark:bg-blue-950/50",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
  photo_deleted: {
    label: "Photo deleted",
    icon: IconTrash,
    bgColor: "bg-red-100 dark:bg-red-950/50",
    iconColor: "text-red-600 dark:text-red-400",
  },
  toe_check_created: {
    label: "Toe check recorded",
    icon: IconChecklist,
    bgColor: "bg-blue-100 dark:bg-blue-950/50",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
  toe_check_signed_off: {
    label: "Toe check signed off",
    icon: IconLock,
    bgColor: "bg-green-100 dark:bg-green-950/50",
    iconColor: "text-green-600 dark:text-green-400",
  },
  toe_check_unlocked: {
    label: "Toe check unlocked",
    icon: IconLockOpen,
    bgColor: "bg-slate-100",
    iconColor: "text-slate-600",
  },
  toe_check_deleted: {
    label: "Toe check deleted",
    icon: IconTrash,
    bgColor: "bg-red-100 dark:bg-red-950/50",
    iconColor: "text-red-600 dark:text-red-400",
  },
  yaw_check_created: {
    label: "YAW check recorded",
    icon: IconChecklist,
    bgColor: "bg-blue-100 dark:bg-blue-950/50",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
  yaw_check_updated: {
    label: "YAW check updated",
    icon: IconChecklist,
    bgColor: "bg-blue-100 dark:bg-blue-950/50",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
  yaw_check_signed_off: {
    label: "YAW check signed off",
    icon: IconLock,
    bgColor: "bg-green-100 dark:bg-green-950/50",
    iconColor: "text-green-600 dark:text-green-400",
  },
  yaw_check_unlocked: {
    label: "YAW check unlocked",
    icon: IconLockOpen,
    bgColor: "bg-slate-100",
    iconColor: "text-slate-600",
  },
  yaw_check_deleted: {
    label: "YAW check deleted",
    icon: IconTrash,
    bgColor: "bg-red-100 dark:bg-red-950/50",
    iconColor: "text-red-600 dark:text-red-400",
  },
};

const DEFAULT_CONFIG = {
  label: "Unknown action",
  icon: IconPlus,
  bgColor: "bg-slate-100",
  iconColor: "text-slate-600",
};

function getDateLabel(date: Date): string {
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "MMM d");
}

function getTimeLabel(date: Date): string {
  return format(date, "h:mm a");
}

interface ActivityLogProps {
  items: AuditLogItem[] | undefined;
  className?: string;
}

export function ActivityLog({ items, className }: ActivityLogProps) {
  const groupedItems = useMemo(() => {
    if (!items || items.length === 0) return [];

    const groups: { label: string; items: AuditLogItem[] }[] = [];
    let currentLabel = "";
    let currentItems: AuditLogItem[] = [];

    for (const item of items) {
      const date = utc(item.created_at);
      const label = getDateLabel(date);

      if (label !== currentLabel) {
        if (currentItems.length > 0) {
          groups.push({ label: currentLabel, items: currentItems });
        }
        currentLabel = label;
        currentItems = [item];
      } else {
        currentItems.push(item);
      }
    }

    if (currentItems.length > 0) {
      groups.push({ label: currentLabel, items: currentItems });
    }

    return groups;
  }, [items]);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <div className="space-y-4">
        {groupedItems.map((group) => (
          <div key={group.label}>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {group.label}
              </span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <div className="bg-card rounded-lg border border-border/50 divide-y divide-border/50">
              {group.items.map((item) => {
                const config = ACTION_CONFIG[item.action] ?? DEFAULT_CONFIG;
                const Icon = config.icon;
                const date = utc(item.created_at);

                return (
                  <div
                    key={item.id}
                    className="px-4 py-2.5 flex items-center gap-3"
                  >
                    <div
                      className={`h-6 w-6 rounded-full ${config.bgColor} flex items-center justify-center flex-shrink-0`}
                    >
                      <Icon className={`${config.iconColor} h-3 w-3`} />
                    </div>
                    <span className="text-[13px] flex-1">{config.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {item.actor}
                    </span>
                    <span className="text-xs text-muted-foreground/50 w-16 text-right tabular-nums">
                      {getTimeLabel(date)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
